import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCreateOrUpdateProfile } from "./useCreateOrUpdateProfile";

export const usePhoneAuth = () => {
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const { toast } = useToast();
  const { createOrUpdateProfile } = useCreateOrUpdateProfile();

  // Phone signup with OTP verification
  const signUpWithPhone = async (
    phoneNumber: string,
    password: string,
    displayName: string
  ) => {
    try {
      setLoading(true);
      setValidationError("");

      // Step 1: Send OTP to phone number using our edge function
      const { data: otpData, error: otpError } = await supabase.functions.invoke('send-sms-invitation', {
        body: {
          phoneNumber: phoneNumber,
          vendorName: displayName,
          isSignup: true // Flag to indicate this is signup, not invitation
        }
      });

      if (otpError) {
        console.error("Error sending OTP:", otpError);
        throw new Error("Failed to send verification code. Please try again.");
      }

      return {
        success: true,
        message: "Verification code sent to your phone",
        otpSent: true
      };
    } catch (error: any) {
      console.error("Phone signup error:", error);
      setValidationError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete signup
  const verifyOTPAndCreateAccount = async (
    phoneNumber: string,
    otpCode: string,
    password: string,
    displayName: string
  ) => {
    try {
      setLoading(true);
      setValidationError("");

      // Create email from phone number for Supabase auth
      // This allows us to use Supabase's built-in auth with a generated email
      const generatedEmail = `${phoneNumber.replace(/\D/g, '')}@phone.auth`;

      // Step 1: Verify OTP through our existing function
      const { data: verifyData, error: verifyError } = await supabase.rpc('verify_and_claim_otp_invitation', {
        p_phone_number: phoneNumber,
        p_otp_code: otpCode,
        p_email: generatedEmail,
        p_password: password,
        p_display_name: displayName
      });

      if (verifyError || !verifyData?.success) {
        throw new Error(verifyData?.message || "Invalid or expired verification code");
      }

      // Step 2: Create Supabase auth user with generated email
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: generatedEmail,
        password: password,
        options: {
          data: {
            display_name: displayName,
            phone_number: phoneNumber,
            auth_method: 'phone'
          }
        }
      });

      if (signUpError) {
        console.error("Supabase signup error:", signUpError);
        throw new Error(signUpError.message);
      }

      // Step 3: Create profile 
      if (signUpData?.user) {
        await createOrUpdateProfile(signUpData.user.id, {
          display_name: displayName,
          email: generatedEmail // Store generated email in profile
        });

        // Add vendor role by default for phone signups
        await supabase.from('user_roles').insert({
          user_id: signUpData.user.id,
          role: 'vendor'
        });
      }

      return {
        success: true,
        message: "Account created successfully",
        user: signUpData.user
      };
    } catch (error: any) {
      console.error("OTP verification error:", error);
      setValidationError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with phone number and password
  const signInWithPhone = async (phoneNumber: string, password: string) => {
    try {
      setLoading(true);
      setValidationError("");

      // Generate email from phone number
      const generatedEmail = `${phoneNumber.replace(/\D/g, '')}@phone.auth`;

      // Sign in using the generated email
      const { data, error } = await supabase.auth.signInWithPassword({
        email: generatedEmail,
        password: password
      });

      if (error) {
        console.error("Phone sign in error:", error);
        throw new Error("Invalid phone number or password");
      }

      return {
        success: true,
        user: data.user,
        session: data.session
      };
    } catch (error: any) {
      console.error("Phone sign in error:", error);
      setValidationError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    signUpWithPhone,
    verifyOTPAndCreateAccount,
    signInWithPhone,
    loading,
    validationError,
    setValidationError
  };
};